import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import config from '../config/environment';
import { accountsLogoutUrl, shouldUseAccounts, SKIP_SSO_AUTO_KEY, type AuthModeConfig } from '../accounts';

// Types
interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  tenant_id: number;
  is_super_admin?: boolean;
}

interface LoginExtras {
  codeVerifier?: string;
  redirectUri?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authConfig: AuthModeConfig;
  login: (code: string, extras?: LoginExtras) => Promise<void>;
  logout: () => void;
  verifyToken: () => Promise<boolean>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authConfig, setAuthConfig] = useState<AuthModeConfig>({ auth_mode: 'google' });

  // Check if token is valid
  const isTokenValid = (token: string): boolean => {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp ? decoded.exp > currentTime : false;
    } catch {
      return false;
    }
  };

  // Extract user from token
  const extractUserFromToken = (token: string): User | null => {
    try {
      const decoded: any = jwtDecode(token);
      return {
        id: decoded.user_id,
        email: decoded.email,
        name: decoded.name || '',
        picture: decoded.picture,
        tenant_id: decoded.tenant_id,
        is_super_admin: Boolean(decoded.is_super_admin),
      };
    } catch {
      return null;
    }
  };

  const clearAuthStorage = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('userRole');
    localStorage.removeItem('metrica_tenant');
    localStorage.removeItem('metrica_auth');
  };

  // Verify token with backend
  const verifyToken = async (): Promise<boolean> => {
    const token = localStorage.getItem('authToken');
    if (!token || !isTokenValid(token)) {
      return false;
    }

    try {
      const response = await fetch(`${config.api.baseUrl}/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();
      return data.success && data.data.valid;
    } catch (error) {
      console.error('Token verification failed:', error);
      return false;
    }
  };

  const login = async (code: string, extras?: LoginExtras): Promise<void> => {
    try {
      const sso = Boolean(extras?.codeVerifier && extras?.redirectUri);
      if (shouldUseAccounts(authConfig) && !sso) {
        throw new Error('SSO login requires PKCE');
      }
      const path = sso ? '/auth/sso' : '/auth/google';
      const body = sso
        ? { code, code_verifier: extras?.codeVerifier, redirect_uri: extras?.redirectUri }
        : { code };

      const response = await fetch(`${config.api.baseUrl}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Authentication failed');
      }

      const { token, user: userData, tenant: tenantData } = data.data;
      
      localStorage.setItem('authToken', token);
      
      const authState = {
        user: userData,
        tenant: tenantData,
        token: token,
        isAuthenticated: true
      };
      localStorage.setItem('metrica_auth', JSON.stringify(authState));
      
      setUser(userData);
      
      window.dispatchEvent(new Event('authStateChanged'));
      
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await fetch(`${config.api.baseUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      const redirectToIdentity = shouldUseAccounts(authConfig);
      const identityUrl = authConfig.identity_url;
      clearAuthStorage();
      sessionStorage.setItem(SKIP_SSO_AUTO_KEY, '1');
      if (redirectToIdentity) {
        // Full-page SLO before React can route to /signin and auto-start OAuth.
        window.location.replace(accountsLogoutUrl(identityUrl));
        return;
      }
      setUser(null);
      window.dispatchEvent(new Event('authStateChanged'));
    }
  };

  useEffect(() => {
    fetch(`${config.api.baseUrl}/auth/config`)
      .then((response) => response.json())
      .then((data) => {
        if (data?.success && data.data) {
          setAuthConfig(data.data);
        }
      })
      .catch((error) => {
        console.warn('Auth config unavailable, defaulting to Google:', error);
      });
  }, []);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        
        if (token && isTokenValid(token)) {
          const userData = extractUserFromToken(token);
          if (userData) {
            const isValid = await verifyToken();
            if (isValid) {
              setUser(userData);
            } else {
              localStorage.removeItem('authToken');
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    authConfig,
    login,
    logout,
    verifyToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
